import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useDebounce from '../../hooks/useDebounce';

import { getCustomerShipToDetails, otherCustomerShipToDetailsForOrder } from '../../redux/actions';

const AddressSearchDialog = props => {
	const shipToAddressListRef = useRef({
		isLoaded: false,
		addresses: []
	});
	const [searchKeyword, setSearchKeyword] = useState('');
	const debouncedSearchKeyword = useDebounce(searchKeyword);
	const [isSearchInProgress, setIsSearchInProgress] = useState(false);
	const [searchResults, setSearchResults] = useState(null);
	const keywordInputRef = useRef();

	const { user } = useSelector(({ user }) => ({ user }));
	const dispatch = useDispatch();

	useEffect(() => {
		keywordInputRef.current && keywordInputRef.current.focus();
	}, []);

	useEffect(() => {
		shipToAddressListRef.current.addresses = props
			.getCombinedAddresses(true)
			.filter(address => address.ACCOUNT_NUMBER === user.selectedAccount.ACCOUNTNUMBER && address.PURPOSE === 'SHIP_TO');
		if (
			user.addressList &&
			user.addressList.responseObject &&
			user.addressList.responseObject.some(address => address.ACCOUNT_NUMBER === user.selectedAccount.ACCOUNTNUMBER) &&
			user.customerShipToDetailsAddressListForOrder &&
			user.customerShipToDetailsAddressListForOrder.some(address => address.ACCOUNT_NUMBER === user.selectedAccount.ACCOUNTNUMBER)
		) {
			shipToAddressListRef.current.isLoaded = true;
		}
	}, [user.addressList, user.customerShipToDetailsAddressListForOrder]);

	useEffect(() => {
		const keyword = debouncedSearchKeyword.trim();
		if (keyword.length >= 2) {
			const addressesWithExactMatch = [];

			const addressesWithPartialMatch = shipToAddressListRef.current.addresses.filter(address => {
				if (address.LOCATION === keyword) {
					addressesWithExactMatch.push(address);
				}
				return address.LOCATION && address.LOCATION.toUpperCase().includes(keyword.toUpperCase());
			});
			if (isNaN(keyword)) {
				if (addressesWithPartialMatch.length) {
					setSearchResults(addressesWithPartialMatch);
				}
			} else {
				if (addressesWithExactMatch.length) {
					setSearchResults(addressesWithExactMatch);
				}
			}
		}
	}, [debouncedSearchKeyword, shipToAddressListRef.current.isLoaded]);

	const fetchShipToAddressesByStoreId = storeId => {
		setIsSearchInProgress(true);
		dispatch(
			getCustomerShipToDetails(`requestType=getLocationByStoreID&accountNumber=${user.selectedAccount.ACCOUNTNUMBER}&storeId=${storeId}`, false, searchResponse => {
				setIsSearchInProgress(false);
				const searchResults = [];
				let foundAddresses = Array.isArray(searchResponse.responseObject)
					? searchResponse.responseObject
					: searchResponse.responseObject && searchResponse.responseObject.records
					? searchResponse.responseObject.records
					: [];
				if (foundAddresses.length) {
					const updatedCustomerShipToDetailsAddressListForOrder = user.customerShipToDetailsAddressListForOrder ? [...user.customerShipToDetailsAddressListForOrder] : [];
					foundAddresses.forEach(shipToAddress => {
						let isValidAddress =
							shipToAddress.ADDRESS4 !== 'JOB SITE' &&
							shipToAddress.PURPOSE &&
							(user.selectedAccount.customerClassCode === 'INTERNATIONAL'
								? shipToAddress.PURPOSE === 'BILL_TO'
									? true
									: shipToAddress.PURPOSE === 'SHIP_TO' && (!['US', 'CA'].includes(shipToAddress.COUNTRY) || shipToAddress.POSTAL_CODE)
								: shipToAddress.PURPOSE === 'SHIP_TO' && shipToAddress.POSTAL_CODE) &&
							shipToAddress.SHIP_TO_ACCT_STATUS === 'A' &&
							shipToAddress.PARTY_SITE_STATUS === 'A' &&
							shipToAddress.SOLD_TO_ACCT_STATUS === 'A' &&
							shipToAddress.STATUS === 'A' &&
							shipToAddress.PURPOSE_STATUS === 'A' &&
							shipToAddress.ACCOUNT_SITE_STATUS === 'A';

						if (isValidAddress) {
							const address = {
								LOCATION: shipToAddress.LOCATION,
								LOCATIONID: shipToAddress.LOCATION,
								POSTALCODE: shipToAddress.POSTAL_CODE,
								SITE_USE_ID: shipToAddress.SITE_USE_ID,
								STATE: shipToAddress.STATE,
								PROVINCE: shipToAddress.PROVINCE,
								SITEID: shipToAddress.ACCOUNT_SITE_ID,
								NAME: shipToAddress.LOCATION,
								STATUS: shipToAddress.PURPOSE_STATUS,
								CITY: shipToAddress.CITY,
								COUNTRY: shipToAddress.COUNTRY,
								PURPOSE: shipToAddress.PURPOSE,
								PURPOSE_STATUS: shipToAddress.PURPOSE_STATUS,
								ACCOUNT_NUMBER: shipToAddress.SOLD_TO_ACCOUNT,
								ADDRESS1: shipToAddress.ADDRESS1,
								ADDRESS2: shipToAddress.ADDRESS2 || '',
								PRIMARYINDICATOR: shipToAddress.PRIMARY_FLAG,
								PARTY_ID: shipToAddress.PARTY_SITE_ID,
								COUNTY: shipToAddress.COUNTY,
								SITENUMBER: parseInt(shipToAddress.PARTY_SITE, 10),
								PARTYSITEID: parseInt(shipToAddress.PARTY_SITE_ID, 10),
								SHIP_TO_ACCOUNT_NUMBER: shipToAddress.SHIP_TO_ACCOUNT,
								SHIP_TO_ACCOUNT_ID: shipToAddress.SHIP_TO_ACCOUNT_ID
							};
							const addressAlreadyInList = updatedCustomerShipToDetailsAddressListForOrder.find(customerShipTo => customerShipTo.SITE_USE_ID === address.SITE_USE_ID);
							if (!addressAlreadyInList) {
								updatedCustomerShipToDetailsAddressListForOrder.push(address);
								searchResults.push(address);
							} else {
								searchResults.push(addressAlreadyInList);
							}
						}
					});

					if ((user.customerShipToDetailsAddressListForOrder || []).length !== updatedCustomerShipToDetailsAddressListForOrder.length) {
						dispatch(otherCustomerShipToDetailsForOrder(updatedCustomerShipToDetailsAddressListForOrder));
					}
				}
				setSearchResults(previousSearchResults => {
					if (previousSearchResults && previousSearchResults.length) {
						const updatedSearchResults = [...previousSearchResults];
						searchResults.forEach(searchResult => {
							if (!updatedSearchResults.some(updatedSearchResult => updatedSearchResult.SITE_USE_ID === searchResult.SITE_USE_ID)) {
								updatedSearchResults.push(searchResult);
							}
						});
						return updatedSearchResults;
					} else {
						return searchResults;
					}
				});
			})
		);
	};

	const handleChangeKeyword = e => {
		setSearchKeyword(e.target.value);
		if (searchResults) {
			setSearchResults(null);
		}
	};

	const handleSearch = e => {
		e.preventDefault();
		if (searchKeyword.trim()) {
			fetchShipToAddressesByStoreId(searchKeyword.trim());
		}
	};

	return (
		<div className={'overlayDialog visible'} id="addressSearchOverlayDialog">
			<div className="blackoutLayer" onClick={props.onClose}></div>
			<div className="overlayDialogWindow">
				<a className="closeButton" onClick={props.onClose}>
					×
				</a>
				<div>
					<h2 className="title">Search Address by Store Number or Location</h2>
				</div>
				<form onSubmit={handleSearch}>
					<input type="text" ref={keywordInputRef} placeholder="Store Number or Location including leading zeros" value={searchKeyword} onChange={handleChangeKeyword} />
					<button disabled={!searchKeyword.trim() || isSearchInProgress}>
						{isSearchInProgress ? (
							<span
								style={{
									display: 'inline-block',
									verticalAlign: 'middle',
									height: '.75rem',
									aspectRatio: '1',
									borderRadius: '50%',
									border: '3px solid #ffffff',
									borderBottomColor: 'transparent',
									animation: 'spin 1s linear infinite',
									boxSizing: 'border-box'
								}}
							/>
						) : (
							'Search'
						)}
					</button>
				</form>
				{searchResults && (
					<div className="addressSearchResults">
						{searchResults &&
							(searchResults.length ? (
								<React.Fragment>
									<ul>
										{searchResults.map((address, index) => (
											<li key={index} onClick={props.onConfirm.bind(this, address)}>
												{[address.ADDRESS1, address.CITY, address.STATE, address.COUNTRY, address.POSTALCODE, address.LOCATION]
													.filter(addressSegment => addressSegment)
													.join(', ')}
											</li>
										))}
									</ul>
								</React.Fragment>
							) : (
								<p className="mb-0 text-center">No Results found.{/^\d+$/.test(searchKeyword.trim()) ? ' Try adding leading zeros.' : ''}</p>
							))}
					</div>
				)}
			</div>
		</div>
	);
};

export default AddressSearchDialog;



// WEBPACK FOOTER //
// ./src/App/components/dialogs/addressSearchDialog.jsx